import { useLayoutEffect, type RefObject } from 'react';

import type { ShapeId, WallShape } from '@walls/store';
import { rectsIntersect, type BoardRenderWindow } from '../../viewport/renderWindow';
import { getShapeBounds } from '../../shapes/geometry/shapeGeometry';
import { clearCanvas, scaleCanvas } from '../utils/canvasScale';
import { drawShape } from '../renderers/drawShape';

interface UseCanvasRendererOptions {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  draftShape?: WallShape | null;
  editableSelectedShapeIds?: ShapeId[];
  height: number;
  renderWindow: BoardRenderWindow;
  selectedShapeIds?: ShapeId[];
  shapes: WallShape[];
  width: number;
  zoom: number;
}

export function useCanvasRenderer({
  canvasRef,
  draftShape,
  editableSelectedShapeIds = [],
  height,
  renderWindow,
  selectedShapeIds = [],
  shapes,
  width,
  zoom,
}: UseCanvasRendererOptions) {
  useLayoutEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas || width <= 0 || height <= 0) {
      return;
    }

    scaleCanvas(canvas, width, height);
    clearCanvas(canvas);

    const context = canvas.getContext('2d');

    if (!context) {
      return;
    }

    const selectedShapeIdSet = new Set(selectedShapeIds);
    const editableSelectedShapeIdSet = new Set(editableSelectedShapeIds);

    context.save();
    context.translate(-renderWindow.x, -renderWindow.y);

    shapes
      .filter((shape) => rectsIntersect(getShapeBounds(shape, context, zoom), renderWindow))
      .sort((a, b) => (a.zPos ?? 0) - (b.zPos ?? 0))
      .forEach((shape) => {
        drawShape(
          context,
          shape,
          zoom,
          selectedShapeIdSet.has(shape.id),
          editableSelectedShapeIdSet.has(shape.id)
        );
      });

    if (draftShape && rectsIntersect(getShapeBounds(draftShape, context, zoom), renderWindow)) {
      drawShape(context, draftShape, zoom, true, true);
    }
    context.restore();
  }, [
    canvasRef,
    draftShape,
    editableSelectedShapeIds,
    height,
    renderWindow,
    selectedShapeIds,
    shapes,
    width,
    zoom,
  ]);
}
